import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Divider, Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';
import API from '../../../../app/api';
import { setSnackbar } from '../../../../store/reducers/globals';
import clsx from 'clsx';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import moment from 'moment/moment';
import _ from 'lodash';
import { getAdmissionStartDate } from '../../../../util/patientUtil';
import AgyleToggleButtons from '../../../../components/ui/buttons/agyleToggleButtons';
import { AgyleNamedIcon } from '../../../../components/icons/agyle';
import Typography from '@mui/material/Typography';
import AgyleLoadingSpinner from '../../../../components/other/agyleLoadingSpinner';
import PatientActivityBox from './patientActivityBox';
import { ctxSplitScreen } from '../../../../store/reducers/patientContext';
import { sendUserAudit } from '../../../../store/reducers/user';
import { AUDIT_TYPES } from '../../../../util/auditUtil';
import usePatientAppointments from '../../../ward/forms/scheduler/scheduledAppointments/usePatientAppointments';
import { SLOT_STATUSES } from '../../../appointments/utils/constants/appointmentSessionConstants';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: 10,
        width: '97vw',
        overflow: 'hidden',
        marginLeft: 10,
    },
    width: {
        width: 'auto',
        paddingTop: 30,
    },
    activityList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: '0 20px',
        maxHeight: '400px',
    },
    chip: {
        backgroundColor: '#ffffff',
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    list: {
        height: 'calc(100% - 70px)',
        overflowY: 'auto',
        padding: '0 10px',
    },
    toggleRoot: {
        color: theme.palette.mode === 'dark' ? '#222222' : '#f3f3f4',
        backgroundColor: theme.palette.mode === 'dark' ? '#313233' : '#eff1f3',
        display: 'flex',
        width: '100%',
        padding: '5px !important',
    },
    toggleLabel: {
        color: theme.palette.mode === 'dark' ? '#ACAFB3' : '#222222',
        height: '25px !important',
        fontSize: 12,
        '&.icon': {
            fontSize: 10,
        },
        '&.Mui-disabled': {
            border: 'none',
        },
    },
    toggleSelected: {
        color: theme.palette.mode === 'dark' ? '#222222 !important' : '#ffffff !important',
        fontWeight: 700,
        backgroundColor: theme.palette.mode === 'dark' ? '#ffffff !important' : '#626466 !important',
    },
}));

const INIT_FILTER = {
    from: null,
    to: null,
    visitNumber: '',
};

const PatientContextActivitySelector = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();

    let { ptId } = useParams();

    const [activities, setActivities] = useState([]);
    const [loadingActivities, setLoadingActivities] = useState(false);

    const [appointmentList, appointmentsLoading] = usePatientAppointments({ hospitalNumber: ptId });

    const availableStatus = [SLOT_STATUSES.NOT_REGISTERED.value, SLOT_STATUSES.DNA.value, SLOT_STATUSES.WNB.value];
    const filtered = appointmentList ? appointmentList.filter((appointment) => availableStatus.includes(appointment.status)) : [];

    const splitScreen = useSelector(ctxSplitScreen);
    const isSSEnabled = splitScreen?.enabled;

    const [filter, setFilter] = useState(INIT_FILTER);
    const [expandAll, setExpandAll] = useState(false);

    useEffect(() => {
        if (ptId) {
            setLoadingActivities(true);
            API.getPatientAttendancesHistory(ptId)
                .then((result) => {
                    setActivities(result);
                })
                .catch((ex) => {
                    console.error(ex);
                    dispatch(
                        setSnackbar({
                            type: 'error',
                            message: t('An error occurred while trying to receiving the activity list.'),
                        }),
                    );
                })
                .finally(() => {
                    setLoadingActivities(false);
                });
        }
    }, [ptId]);

    const handleLoadActivity = async (param, cardNotExists) => {
        const attendanceId = param?.admission?.__id;
        if (cardNotExists && attendanceId) {
            await API.findPatientInWard({ attendanceId });
        }

        if (attendanceId) {
            dispatch(sendUserAudit(param, AUDIT_TYPES.OPEN_HISTORICAL_ACTIVITY));
            history.push(`/patient/attendance/${attendanceId}`);
        }
    };

    const handleFilter = (activities) => {
        const filteredActivities = _.filter(activities).filter((att) => {
            let valid = true;
            const admissionStartDate = getAdmissionStartDate(att.admission);

            if (filter.visitNumber) {
                valid = valid && att.admission?.attendanceId === filter.visitNumber;
            }

            if (filter.from) {
                valid = valid && admissionStartDate.isSameOrAfter(filter.from, 'day');
            }

            if (filter.to) {
                valid = valid && admissionStartDate.isSameOrBefore(filter.to, 'day');
            }

            return valid;
        });
        const filteredAppointments = _.filter(filtered).filter((att) => {
            if (!filter.visitNumber) {
                let valid = true;
                const startDate = moment(att.startDate);

                if (filter.from) {
                    valid = valid && startDate.isSameOrAfter(filter.from, 'day');
                }
                if (filter.to) {
                    valid = valid && startDate.isSameOrBefore(filter.to, 'day');
                }
                return valid;
            }
            return false;
        });

        return _.orderBy(
            [...filteredActivities, ...filteredAppointments],
            (a) => {
                if (a.startDate) return moment(a.startDate);
                else return getAdmissionStartDate(a.admission);
            },
            ['desc'],
        );
    };

    return (
        <>
            <Grid container direction="row" alignContent="center" className={clsx(classes.root, isSSEnabled && classes.width)}>
                <Grid item xs={12}>
                    <div className={classes.divRow} style={{ flexWrap: isSSEnabled ? 'wrap' : 'nowrap' }}>
                        <Grid item xs={isSSEnabled ? 12 : 4}>
                            <AgyleTextField
                                label={t('Visit Number')}
                                onChange={(event) => {
                                    setFilter((formData) => ({
                                        ...formData,
                                        visitNumber: event.target.value,
                                    }));
                                }}
                                value={filter.visitNumber}
                                fullWidth
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>

                        <Grid item xs={isSSEnabled ? 12 : 3}>
                            <AgyleDatePicker
                                label={t('From Date')}
                                onChange={(value) => {
                                    setFilter((formData) => ({
                                        ...formData,
                                        from: value,
                                    }));
                                }}
                                value={filter.from}
                                type="date"
                                maxDate={moment()}
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>

                        <Grid item xs={isSSEnabled ? 12 : 3}>
                            <AgyleDatePicker
                                label={t('To Date')}
                                onChange={(value) => {
                                    setFilter((formData) => ({
                                        ...formData,
                                        to: value,
                                    }));
                                }}
                                value={filter.to}
                                type="date"
                                noBorder={false}
                                withNewBackground
                                disableFuture={false}
                            />
                        </Grid>

                        <Grid item xs={isSSEnabled ? 12 : 1}>
                            <AgyleToggleButtons
                                classNameRoot={clsx(classes.toggleRoot, 'header')}
                                classNameLabel={clsx(classes.toggleLabel, 'icon')}
                                classNameSelected={classes.toggleSelected}
                                onChange={(val) => {
                                    if (_.isBoolean(val)) {
                                        setExpandAll(val);
                                    }
                                }}
                                value={expandAll}
                                size="small"
                                options={[
                                    {
                                        value: false,
                                        label: <AgyleNamedIcon className={classes.icon} name="collapseAll" />,
                                    },
                                    {
                                        value: true,
                                        label: <AgyleNamedIcon className={classes.icon} name="expandAll" />,
                                    },
                                ]}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={isSSEnabled ? 12 : 1}>
                            <AgyleButton fullWidth variant="contained" onClick={() => setFilter(INIT_FILTER)} style={{ width: '100%' }}>
                                {t('Clear filter')}
                            </AgyleButton>
                        </Grid>
                    </div>

                    <Divider sx={{ m: 2 }} />
                </Grid>
            </Grid>

            <div className={clsx(classes.list, 'scroll-normal-white')}>
                {loadingActivities && <AgyleLoadingSpinner label={t('Loading Patient activities')} />}

                {!loadingActivities &&
                    handleFilter(activities)?.map((a) => (
                        <PatientActivityBox
                            key={a.__id || a?.admission?.__id}
                            admission={a}
                            expandFromParent={expandAll}
                            onOpen={handleLoadActivity}
                        />
                    ))}

                {!loadingActivities && handleFilter(activities)?.length === 0 && (
                    <Typography variant="h6" fontWeight="bold">
                        {t('No activities found.')}
                    </Typography>
                )}
            </div>
        </>
    );
};

export default PatientContextActivitySelector;
